<template>
<transition name="fade" mode="out-in">
  <div v-if="!started" class="mx-auto pb-4 px-4">
    <p class="mb-3">Welcome to the Employee Confidence Pulse Survey!</p>
    <p class="mb-3">This survey will only take you a couple of minutes to complete and has been designed to provide a snapshot of how confident employees are about their organisation’s potential for current and future success.</p>
    <p class="mb-3">Rest assured that your responses will be kept both confidential and secure at all times.</p>
    <p class="mb-3">Begin the survey to receive your individual results benchmarked against employees from across the country.</p>
    <div class="mt-6 text-center">
      <button class="mx-2 text-white rounded px-8 py-2 uppercase tracking-wide font-semibold"  @click="started = true" :style=" {backgroundColor: brandColor}">Begin the survey</button>
    </div>
  </div>
<form v-else @submit.prevent="handleSubmit" class="mx-auto pb-4">
    <p v-if="formvalid">You need to enter some details.</p>
    <div v-if="alreadyDone" class="my-6 text-center1">
      <div>
        <div class="bg-red-lightest border-l-4 border-red text-red-dark p-4" role="alert">
          <p class="font-bold">You have already completed this survey!</p>
        </div>
      </div>
    </div>
    <p class="font-bold text-red mb-4 text-sm">Any of the sections below with fewer than five questionnaire responses will not be visible to your employer to ensure your confidentiality.</p>
    <div class="pb-2">
      <label class="text-grey-darker text-sm font-medium" for="email">E-mail</label><br>
      <input class="bg-grey-lighter text-grey-darkest rounded p-2 w-full" id="email" type="email" v-model="email" required>
    </div>
    <div>
      <p class="text-sm text-grey-darker mb-4">*Email address is only used by the survey software to ensure that surveys are only completed once per respondent. Your individual responses are kept confidential and secure and are not visible to employers or anyone else.</p>
    </div>
    <div class="pb-6">
      <label class="text-grey-darker text-sm font-medium" for="organisation">Organisation</label><br>
      <input class="bg-grey-lighter text-grey-darkest rounded p-2 w-full" id="organisation" type="text" v-model="organisation" required>
    </div>
    <fieldset class="pb-6">
      <legend class="text-grey-darker text-sm font-medium">Gender</legend>
      <div>
        <input value="male" type="radio" id="huey" name="drone" v-model="gender" checked />
        <label for="huey">Male</label>
      </div>
      <div>
        <input value="female" type="radio" id="dewey" name="drone" v-model="gender" />
        <label for="dewey">Female</label>
      </div>
      <div>
        <input value="other" type="radio" id="louie" name="drone" v-model="gender" />
        <label for="louie">Prefer to Self Define</label>
      </div>
    </fieldset>
    <div class="pb-6" v-if="gender == 'other'">
      <label class="text-grey-darker text-sm font-medium" for="otherGender">Other gender</label><br>
      <input class="bg-grey-lighter text-grey-darkest rounded p-2 w-full" id="otherGender" type="text" v-model="otherGender" required>
    </div>
    <div class="pb-6">
      <label class="text-grey-darker text-sm font-medium" for="age">Age</label><br>
      <select class="bg-grey-lighter text-grey-darkest rounded p-2 w-full appearance-none" id="age" v-model="age" required>
        <option value="" disabled selected>Please select an option</option>
        <option value="1">17 or younger.</option>
        <option value="2">18-24</option>
        <option value="3">25-34</option>
        <option value="4">35-44</option>
        <option value="5">45-54</option>
        <option value="6">55-64</option>
        <option value="7">65-74</option>
        <option value="8">75 or older</option>
        <option value="9">Prefer not to say</option>
      </select>
    </div>
    <div class="pb-6">
      <label class="text-grey-darker text-sm font-medium" for="region">Region</label><br>
      <select class="bg-grey-lighter text-grey-darkest rounded p-2 w-full appearance-none" id="region" v-model="region" required>
        <option value="" disabled selected>Please select an option</option>
        <option value="Northland">Northland</option>
        <option value="Auckland">Auckland</option>
        <option value="Waikato">Waikato</option>
        <option value="Bay of Plenty">Bay of Plenty</option>
        <option value="Gisborne">Gisborne</option>
        <option value="Hawke’s Bay">Hawke’s Bay</option>
        <option value="Taranaki">Taranaki</option>
        <option value="Manawatu-Wanganui">Manawatu-Wanganui</option>
        <option value="Wellington">Wellington</option>
        <option value="Nelson-Marlborough">Nelson-Marlborough</option>
        <option value="West Coast">West Coast</option>
        <option value="Canterbury">Canterbury</option>
        <option value="Otago">Otago</option>
        <option value="Southland">Southland</option>
      </select>
    </div>
    <div class="pb-6">
      <label class="text-grey-darker text-sm font-medium" for="tenure">How long have you been with the organisation?</label><br>
      <select class="bg-grey-lighter text-grey-darkest rounded p-2 w-full appearance-none" id="tenure" v-model="tenure" required>
        <option value="" disabled selected>Please select an option</option>
        <option value="1">Less than 1 Year</option>
        <option value="2">1 – 2 Years</option>
        <option value="3">2 – 3 Years</option>
        <option value="4">3 – 5 Years</option>
        <option value="5">5 – 10 Years</option>
        <option value="6">10 Years+</option>
      </select>
    </div>
    <div class="pb-6">
      <label class="text-grey-darker text-sm font-medium" for="position">Role</label><br>
      <select class="bg-grey-lighter text-grey-darkest rounded p-2 w-full appearance-none" id="position" v-model="position" required>
        <option value="" disabled selected>Please select an option</option>
        <option value="1">Senior Management / Executive</option>
        <option value="2">Management / Leadership Role</option>
        <option value="3">Employee</option>
      </select>
    </div>
    <div class="pb-6">
      <label class="text-grey-darker text-sm font-medium" for="department">Department</label><br>
      <input placeholder="IT, workshop etc" class="bg-grey-lighter text-grey-darkest rounded p-2 w-full" id="department" type="department" v-model="department" required>
    </div>
    <button class="mx-2 text-white rounded px-8 py-2 uppercase tracking-wide font-semibold" type="submit" :style="{backgroundColor: brandColor}">Next</button>
  </form>
</transition>
</template>

<script>
import axios from 'axios'

export default {
  name: 'generic',
  props: ['company', 'img', 'brandColor'],
  beforeCreate () {
    this.url = 'https://employeeconfidence.nz/api'
    // axios.get(`${this.url}/company/${this.$route.params.link}`)
    //   .then(response => {
    //     this.company = response.data.name
    //     this.img = response.data.logo
    //     localStorage.setItem('company', JSON.stringify({ name: response.data.name, logo: response.data.logo }))
    //   })
    //   .catch(e => {
    //     console.log(e)
    //   })
  },
  mounted () {
    console.log(process.env.APP_PATH, process.env.NODE_ENV)
  },
  data: () => ({
    started: false,
    valid: false,
    formvalid: false,
    alreadyDone: false,
    organisation: '',
    organisationRules: [
      v => !!v || 'Organisation is required'
    ],
    position: '',
    positionRules: [
      v => !!v || 'Position is required'
    ],
    tenure: '',
    tenureRules: [
      v => !!v || 'Tenure is required'
    ],
    gender: '',
    genderRules: [
      v => !!v || 'Gender is required'
    ],
    email: '',
    emailRules: [
      v => !!v || 'E-mail is required',
      v => /.+@.+/.test(v) || 'E-mail must be valid'
    ],
    age: '',
    region: '',
    department: ''
  }),
  methods: {
    handleSubmit () {
      if (this.email && this.organisation && this.position && this.tenure && this.gender) {

      } else {
        this.formvalid = true
        return
      }

      var computedGender = this.gender === 'other' ? this.otherGender : this.gender
      axios.post(`${process.env.APP_PATH}/company/verify/generic`, {
        email: this.email,
        age: this.age,
        gender: computedGender,
        position: this.position,
        tenure: this.tenure,
        organisation: this.organisation,
        type: 'generic'
      })
        .then(response => {
          // JSON responses are automatically parsed.
          console.log('', response.data)
          if (!response.data) {
            let def = 5
            let obj = {
              email: this.email,
              organisation: this.organisation,
              gender: computedGender,
              age: this.age,
              position: this.position,
              region: this.region,
              department: this.department,
              tenure: this.tenure,
              ans_1: def,
              ans_2: def,
              ans_3: def,
              ans_4: def,
              ans_5: def,
              ans_6: def,
              ans_7: def,
              ans_8: def,
              ans_9: def,
              ans_10: def,
              ans_11: def,
              ans_12: def
            }
            localStorage.setItem('user-data', JSON.stringify(obj))
            localStorage.setItem('surveyType', 'generic')
            localStorage.setItem('surveySubmit', false)
            console.log('route here please', this.$router)
            this.$router.push({name: 'question', params: { qno: 1 }})
          } else {
            this.alreadyDone = true
          }
        })
        .catch(e => {
          console.log(e)
        })
    }
  }
}
</script>

<style>
  .v-card {
    background: white;
  }
  .myButton {
    -moz-box-shadow: 0px 10px 14px -7px #276873;
    -webkit-box-shadow: 0px 10px 14px -7px #276873;
    box-shadow: 0px 10px 14px -7px #276873;
    background:-webkit-gradient(linear, left top, left bottom, color-stop(0.05, #599bb3), color-stop(1, #408c99));
    background:-moz-linear-gradient(top, #599bb3 5%, #408c99 100%);
    background:-webkit-linear-gradient(top, #599bb3 5%, #408c99 100%);
    background:-o-linear-gradient(top, #599bb3 5%, #408c99 100%);
    background:-ms-linear-gradient(top, #599bb3 5%, #408c99 100%);
    background:linear-gradient(to bottom, #599bb3 5%, #408c99 100%);
    filter:progid:DXImageTransform.Microsoft.gradient(startColorstr='#599bb3', endColorstr='#408c99',GradientType=0);
    background-color:#599bb3;
    -moz-border-radius:8px;
    -webkit-border-radius:8px;
    border-radius:8px;
    display:inline-block;
    cursor:pointer;
    color:#ffffff;
    font-family:Arial;
    font-size:20px;
    font-weight:bold;
    padding:13px 32px;
    text-decoration:none;
    text-shadow:0px 1px 0px #3d768a;
  }
  .myButton:hover {
    background:-webkit-gradient(linear, left top, left bottom, color-stop(0.05, #408c99), color-stop(1, #599bb3));
    background:-moz-linear-gradient(top, #408c99 5%, #599bb3 100%);
    background:-webkit-linear-gradient(top, #408c99 5%, #599bb3 100%);
    background:-o-linear-gradient(top, #408c99 5%, #599bb3 100%);
    background:-ms-linear-gradient(top, #408c99 5%, #599bb3 100%);
    background:linear-gradient(to bottom, #408c99 5%, #599bb3 100%);
    filter:progid:DXImageTransform.Microsoft.gradient(startColorstr='#408c99', endColorstr='#599bb3',GradientType=0);
    background-color:#408c99;
  }
  .myButton:active {
    position:relative;
    top:1px;
  }
</style>



// WEBPACK FOOTER //
// src/components/Generic.vue